import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  DestroyRef,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { FormlyModule, FormlyFieldConfig } from "@ngx-formly/core";
import { Observable, of, forkJoin } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import {
  faSave,
  faChevronDown,
  faChevronUp,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

// API Services
import { CompanyService } from "../../../api/services/company.service";
import { MatchCompetencyService } from "../../../api/services/match-competency.service";
import { AreaOfInterestService } from "../../../api/services/area-of-interest.service";

// API Models
import { Company } from "../../../api/models/company";
import { CompanyAccount } from "../../../api/models/company-account";
import { MatchCriterion } from "../../../api/models/match-criterion";
import { MatchCriterionCompetency } from "../../../api/models/match-criterion-competency";
import { MatchCompetency } from "../../../api/models/match-competency";
import { ExperienceDay } from "../../../api/models/experience-day";
import { AreaOfInterest } from "../../../api/models/area-of-interest";
import { Address } from "../../../api/models/address";

// Shared Services
import { LocaleService } from "../../../shared/services/locale.service";
import {
  alertMessages,
  AlertService,
} from "../../../shared/services/alert.service";
import { empty } from "../../../functions";

// Shared Components
import { SelectorAreaofinterestsComponent } from "../../../core/selector-areaofinterests/selector-areaofinterests.component";

// Standalone Components
import { CompanyAccountsTableComponent } from "./components/company-accounts-table.component";
import { ExperienceDaysTableComponent } from "./components/experience-days-table.component";

// Form Config Service
import { CompanyFormConfigService } from "../services/company-form.config";

@Component({
  selector: "app-modal-admin-companyprofile",
  templateUrl: "./modal-admin-company.component.html",
  styleUrls: ["./modal-admin-company.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    FontAwesomeModule,
    FormlyModule,
    SelectorAreaofinterestsComponent,
    CompanyAccountsTableComponent,
    ExperienceDaysTableComponent,
  ],
})
export class ModalAdminCompanyComponent implements OnInit {
  // Inject dependencies
  readonly activeModal = inject(NgbActiveModal);
  private readonly companyService = inject(CompanyService);
  private readonly matchCompetencyService = inject(MatchCompetencyService);
  private readonly areaOfInterestService = inject(AreaOfInterestService);
  readonly locale = inject(LocaleService);
  readonly alertService = inject(AlertService);
  private readonly destroyRef = inject(DestroyRef);
  readonly formConfigService = inject(CompanyFormConfigService);

  // Data input from modal
  data!: { entity: Company };

  activeTab = 'general';

  // Icons
  readonly icons = {
    save: faSave,
    chevronDown: faChevronDown,
    chevronUp: faChevronUp,
    infoCircle: faInfoCircle,
  } as const;

  // Entity signal
  entity = signal<Company>({} as Company);

  // Formly fields for General tab
  generalFields: FormlyFieldConfig[] = [];

  // Education level selector (Formly)
  readonly eduLevelsForm = new FormGroup({});
  readonly eduLevelFields: FormlyFieldConfig[] = [{ key: 'educationLevels', type: 'education-level-selector', props: { excludedRegions: ['EU'] } }];
  // Computed so the [model] reference is stable between CD cycles where the
  // entity hasn't changed. A plain getter would return a fresh object on every
  // access, making <formly-form> see [model] as "changed" every CD and resync
  // its internal model — that resync raced with formControl.setValue() in the
  // education-level-selector toggle and visually swallowed the click.
  readonly eduLevelsModel = computed(() => ({ educationLevels: this.entity().educationLevels ?? [] }));
  onEduLevelsChange(m: Record<string, unknown>): void { this.updateEntityField('educationLevels', m['educationLevels'] as Company['educationLevels']); }

  // State signals
  companyAccounts = signal<CompanyAccount[]>([]);
  areaOfInterests = signal<AreaOfInterest[]>([]);
  matchCompetency = signal<MatchCompetency[]>([]);
  matchcriterion = signal<MatchCriterion>({
    companyCultureFactor: 0,
    personalityFactor: 0,
    areaOfInterestFactor: 0,
    companyCultureCompetencies: [],
    personalityCompetencies: [],
  });

  // View state signals
  formLoading = signal(true);
  experienceDaysLoading = signal(false);
  experienceDays = signal<ExperienceDay[]>([]);

  // Computed signals for filtered competencies
  matchCompetencyCompanyCulture = computed(() =>
    this.matchCompetency().filter((c) => c.type === "CompanyCulture")
  );
  matchCompetencyPersonality = computed(() =>
    this.matchCompetency().filter((c) => c.type === "Personality")
  );

  // Type alias for competency array property keys
  readonly companyCultureCompetencies = "companyCultureCompetencies" as const;
  readonly personalityCompetencies = "personalityCompetencies" as const;

  private readonly maxCompanyCultureCompetencies = 5;
  private readonly maxPersonalityCompetencies = 2;

  ngOnInit(): void {
    this.entity.set({ ...this.data.entity } as Company);

    // Load reference data first, then build Formly fields
    this.formConfigService
      .loadReferenceData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.generalFields = this.formConfigService.buildGeneralFields();
        this.formLoading.set(false);
      });

    // Load area of interests (for Match tab)
    this.areaOfInterestService
      .getAreaOfInterests()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.areaOfInterests.set(response?.data ?? []);
      });

    // Load match competencies
    this.loadMatchCompetencies();

    // When item exists, load related company profile data
    const entityId = this.entity().id;
    if (!empty(entityId)) {
      this.getCompanyAccounts(entityId!);
      this.getMatchcriterion(entityId!);
      this.getCompanyExperiencedays(entityId!);
    }
  }

  getCompanyAccounts(id: string): void {
    this.companyService
      .getCompanyAccounts({ companyId: id })
      .pipe(
        catchError(this.handleError("getCompanyAccounts", [])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.companyAccounts.set(result ?? []);
      });
  }

  saveAdditionalData(id: string): Observable<boolean[]> {
    return forkJoin([
      this.companyService
        .setCompanyAccounts({ companyId: id, body: this.companyAccounts() })
        .pipe(
          map(() => true),
          catchError(() => of(false))
        ),
      this.companyService
        .updateCompanyMatchCriterion({
          companyId: id,
          body: this.matchcriterion(),
        })
        .pipe(
          map(() => true),
          catchError(() => of(false))
        ),
      this.companyService
        .setCompanyAreaOfInterest({
          companyId: id,
          body: this.entity().areaOfInterests ?? [],
        })
        .pipe(
          map(() => true),
          catchError(() => of(false))
        ),
    ]).pipe(takeUntilDestroyed(this.destroyRef));
  }

  loadMatchCompetencies(): void {
    this.matchCompetencyService
      .getMatchCompetencies()
      .pipe(
        catchError(this.handleError("getMatchCompetencies", null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        if (response?.data) {
          this.matchCompetency.set(response.data);
        }
      });
  }

  getMatchcriterion(companyId: string): void {
    this.companyService
      .getCompanyMatchCriterion({ companyId })
      .pipe(
        catchError(this.handleError("getCompanyMatchCriterion", null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        if (response) {
          this.matchcriterion.set({ ...response });
        }
      });
  }

  onFactorChange(factor: string): void {
    const criterion = this.matchcriterion();
    const companyCulture = criterion.companyCultureFactor ?? 0;
    const personality = criterion.personalityFactor ?? 0;
    const areaOfInterest = criterion.areaOfInterestFactor ?? 0;
    const totalFactor = companyCulture + personality + areaOfInterest;

    if (totalFactor <= 100) return;

    switch (factor) {
      case "companyCultureFactor":
        this.matchcriterion.update((c) => ({
          ...c,
          companyCultureFactor: 100 - (areaOfInterest + personality),
        }));
        break;
      case "personalityFactor":
        this.matchcriterion.update((c) => ({
          ...c,
          personalityFactor: 100 - (companyCulture + areaOfInterest),
        }));
        break;
      case "areaOfInterestFactor":
        this.matchcriterion.update((c) => ({
          ...c,
          areaOfInterestFactor: 100 - (companyCulture + personality),
        }));
        break;
    }
  }

  // Helper method to get competencies array by type
  private getCompetenciesArray(
    type: "companyCultureCompetencies" | "personalityCompetencies"
  ): MatchCriterionCompetency[] {
    return this.matchcriterion()[type] ?? [];
  }

  // Helper method to set competencies array by type
  private setCompetenciesArray(
    type: "companyCultureCompetencies" | "personalityCompetencies",
    value: MatchCriterionCompetency[]
  ): void {
    this.matchcriterion.update((c) => ({ ...c, [type]: value }));
  }

  getMatchCompetenciesByType(type: string): MatchCompetency[] {
    return this.matchCompetency().filter((comp) => comp.type === type);
  }

  getCompetencyItem(competenceId: string): MatchCompetency | null {
    return (
      this.matchCompetency().find(
        (competency) => competency.id === competenceId
      ) ?? null
    );
  }

  toggleMatchtestCompetence(
    competenceId: string,
    type: "companyCultureCompetencies" | "personalityCompetencies"
  ): void {
    const competencies = this.getCompetenciesArray(type);

    if (!this.hasMatchtestCompetenceValue(competenceId, type)) {
      // Check max limits
      if (
        type === "companyCultureCompetencies" &&
        competencies.length >= this.maxCompanyCultureCompetencies
      ) {
        return;
      }
      if (
        type === "personalityCompetencies" &&
        competencies.length >= this.maxPersonalityCompetencies
      ) {
        return;
      }

      const competencyItem = this.getCompetencyItem(competenceId);
      if (competencyItem) {
        const newCompetencies = [
          ...competencies,
          {
            matchCompetencyId: competencyItem.id!,
            name: competencyItem.name,
            description: competencyItem.description,
            sort: competencies.length + 1,
          },
        ];
        this.setCompetenciesArray(type, newCompetencies);
      }
    } else {
      // Remove item
      const updatedCompetencies = competencies.filter(
        (comp) => comp.matchCompetencyId !== competenceId
      );
      this.setCompetenciesArray(type, updatedCompetencies);
    }
  }

  hasMatchtestCompetenceValue(
    competenceId: string,
    type: "companyCultureCompetencies" | "personalityCompetencies"
  ): boolean {
    if (empty(type)) return false;
    const competencies = this.getCompetenciesArray(type);
    return competencies.some((comp) => comp.matchCompetencyId === competenceId);
  }

  sortMatchcriterionCompetencies(
    type: "companyCultureCompetencies" | "personalityCompetencies"
  ): void {
    const competencies = [...this.getCompetenciesArray(type)];
    competencies.sort((a, b) => a.sort - b.sort);
    this.setCompetenciesArray(type, competencies);
  }

  moveMatchcriterionCompetencyUp(
    key: number,
    type: "companyCultureCompetencies" | "personalityCompetencies"
  ): void {
    if (key > 0) {
      const competencies = [...this.getCompetenciesArray(type)];
      const currentSort = competencies[key].sort;
      competencies[key] = {
        ...competencies[key],
        sort: competencies[key - 1].sort,
      };
      competencies[key - 1] = { ...competencies[key - 1], sort: currentSort };
      this.setCompetenciesArray(type, competencies);
      this.sortMatchcriterionCompetencies(type);
    }
  }

  moveMatchcriterionCompetencyDown(
    key: number,
    type: "companyCultureCompetencies" | "personalityCompetencies"
  ): void {
    const competencies = [...this.getCompetenciesArray(type)];
    if (key < competencies.length - 1) {
      const currentSort = competencies[key].sort;
      competencies[key] = {
        ...competencies[key],
        sort: competencies[key + 1].sort,
      };
      competencies[key + 1] = { ...competencies[key + 1], sort: currentSort };
      this.setCompetenciesArray(type, competencies);
      this.sortMatchcriterionCompetencies(type);
    }
  }

  Save(): void {
    const currentEntity = this.entity();
    const save$ = !empty(currentEntity.id)
      ? this.companyService.updateCompany({
          id: currentEntity.id!,
          body: currentEntity,
        })
      : this.companyService.createCompany({ body: currentEntity });

    save$
      .pipe(
        switchMap((result) => {
          const id = currentEntity.id || result?.id;
          return id ? this.saveAdditionalData(id) : of([false]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (values) => {
          if (values.every((v) => v)) {
            this.alertService.success(alertMessages.successDataSaved);
            this.activeModal.close(true);
          } else {
            this.alertService.errorWithReport(new Error('saveAdditionalData returned false'), 'adminCompany:save');
          }
        },
        error: (err) => this.alertService.errorWithReport(err, 'adminCompany:save'),
      });
  }

  getCompanyExperiencedays(companyId: string): void {
    this.experienceDaysLoading.set(true);
    this.companyService
      .getCompanyExperienceDays({ companyId })
      .pipe(
        catchError(this.handleError("getCompanyExperienceDays", null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        this.experienceDaysLoading.set(false);
        this.experienceDays.set(response?.data ?? []);
      });
  }

  // Helper for two-way binding with entity signal
  updateEntityField<K extends keyof Company>(
    field: K,
    value: Company[K]
  ): void {
    this.entity.update((e) => ({ ...e, [field]: value }));
  }

  // Handler for Formly model changes
  onModelChange(model: Company): void {
    this.entity.set({ ...model });
  }

  // Helper for updating nested visitAddress
  updateVisitAddressField<K extends keyof Address>(
    field: K,
    value: Address[K]
  ): void {
    this.entity.update((e) => ({
      ...e,
      visitAddress: { ...e.visitAddress!, [field]: value },
    }));
  }

  // Helper for matchcriterion two-way binding
  updateMatchcriterionField<K extends keyof MatchCriterion>(
    field: K,
    value: MatchCriterion[K]
  ): void {
    this.matchcriterion.update((c) => ({ ...c, [field]: value }));
  }

  handleError<T>(operation = "", result?: T) {
    return (error: unknown): Observable<T> => {
      switch (operation) {
        case "getCompanyAccounts":
        case "getCompanyMatchCriterion":
        case "getMatchCompetencies":
        case "getCompanyExperienceDays":
          // Silent fail for loading operations
          break;
        default:
          this.alertService.errorWithReport(error, `adminCompany:${operation}`);
          break;
      }

      return of(result as T);
    };
  }
}
